import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { ShieldCheck, FileDown, CheckCircle2, MinusCircle, XCircle, CircleDashed } from "lucide-react";
import { KPI } from "@/components/Primitives";
import DrillPanel from "@/components/DrillPanel";

// CEO view: compliance posture and ISO/IEC 27001 evidence, computed live from
// the estate. The control table is the same data the ISO evidence report PDF
// is built from, so the number on screen and the number in the audit pack agree.

const STATUS_META = {
  met: { icon: CheckCircle2, cls: "text-emerald-400", label: "Met" },
  partial: { icon: MinusCircle, cls: "text-amber-400", label: "Partial" },
  gap: { icon: XCircle, cls: "text-red-400", label: "Gap" },
  not_evaluated: { icon: CircleDashed, cls: "text-zinc-600", label: "No data" },
};

function Donut({ score }) {
  const r = 46, c = 2 * Math.PI * r, off = c - (score / 100) * c;
  const col = score >= 85 ? "#10b981" : score >= 60 ? "#f59e0b" : "#ef4444";
  return (
    <svg viewBox="0 0 120 120" className="w-36 h-36">
      <circle cx="60" cy="60" r={r} fill="none" stroke="#18181b" strokeWidth="10" />
      <circle cx="60" cy="60" r={r} fill="none" stroke={col} strokeWidth="10" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={off} transform="rotate(-90 60 60)" />
      <text x="60" y="58" textAnchor="middle" fill="#fafafa" fontSize="26" fontFamily="monospace" fontWeight="600">{score}</text>
      <text x="60" y="76" textAnchor="middle" fill="#71717a" fontSize="9" fontFamily="monospace" letterSpacing="2">SCORE</text>
    </svg>
  );
}

export default function ExecCompliance() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [drill, setDrill] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);

  const load = () => api.get("/exec/compliance-summary")
    .then(r => { setD(r.data); setErr(false); })
    .catch(() => setErr(true));
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);

  if (err) return <div className="p-8 text-zinc-500 text-sm">Could not load the compliance summary.</div>;
  if (!d) return <div className="p-8 text-zinc-500 text-sm">Loading compliance posture…</div>;

  const c = d.controls || {};
  const cov = d.coverage || {};
  const downloadPdf = () => window.open(`${API}/reports/iso27001/pdf`, "_blank");

  return (
    <div className="p-6 md:p-8 max-w-[1200px] mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
            <ShieldCheck className="text-emerald-400" size={18} />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-zinc-50">Compliance Command</h1>
            <div className="text-xs text-zinc-500 uppercase tracking-widest font-mono">CEO view · {d.framework}</div>
          </div>
        </div>
        <button onClick={downloadPdf} data-testid="download-iso-pdf"
          className="inline-flex items-center gap-2 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 rounded-md px-3.5 py-2 text-sm transition-colors">
          <FileDown size={15} /> Download ISO 27001 evidence (PDF)
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-6">
        <div className="bg-zinc-950 border border-zinc-900 rounded-md p-4 flex items-center justify-center">
          <Donut score={d.compliance_score} />
        </div>
        <div onClick={() => setStatusFilter(f => f === "met" ? null : "met")} className="cursor-pointer"><KPI testid="cmp-met" label="Controls met" value={c.met ?? 0} tone="good" sub={statusFilter === "met" ? "filtering ↓" : `of ${c.controls_total ?? 0} Annex A controls`} /></div>
        <div onClick={() => setStatusFilter(f => f === "partial" ? null : "partial")} className="cursor-pointer"><KPI testid="cmp-partial" label="Partial" value={c.partial ?? 0} tone="warn" sub={statusFilter === "partial" ? "filtering ↓" : "in progress"} /></div>
        <div onClick={() => setStatusFilter(f => f === "gap" ? null : "gap")} className="cursor-pointer"><KPI testid="cmp-gap" label="Gaps" value={c.gap ?? 0} tone={c.gap ? "bad" : "good"} sub={statusFilter === "gap" ? "filtering ↓" : `${c.not_evaluated ?? 0} awaiting data`} /></div>
      </div>

      {/* coverage strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
        <div onClick={() => setDrill("inventory_missing")} className="cursor-pointer"><KPI testid="cov-inv" label="Inventory" value={cov.inventory ?? 0} unit="%" tone={cov.inventory >= 90 ? "good" : "warn"} /></div>
        <div onClick={() => setDrill("patch_gap")} className="cursor-pointer"><KPI testid="cov-patch" label="Patched" value={cov.patched ?? 0} unit="%" tone={cov.patched >= 90 ? "good" : "warn"} /></div>
        <div onClick={() => setDrill("encryption_gap")} className="cursor-pointer"><KPI testid="cov-enc" label="Encrypted" value={cov.encryption ?? 0} unit="%" tone={cov.encryption >= 100 ? "good" : "warn"} /></div>
        <KPI testid="cov-backup" label="Backup runs" value={cov.backup_runs ?? 0} tone={cov.backup_runs ? "good" : "bad"} />
        <KPI testid="cov-evidence" label="Evidence" value={cov.evidence_snapshots ?? 0} sub={`${cov.audit_entries ?? 0} audit rows`} />
      </div>

      {/* control table */}
      <div className="bg-zinc-950 border border-zinc-900 rounded-md mt-4 overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-900 flex items-center justify-between">
          <span className="text-sm font-medium text-zinc-200">Annex A control coverage</span>
          {statusFilter && <button onClick={() => setStatusFilter(null)} className="text-[11px] text-zinc-500 hover:text-zinc-300">Showing {statusFilter} only · clear filter ✕</button>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono border-b border-zinc-900">
                <th className="text-left px-5 py-2.5 font-medium">Control</th>
                <th className="text-left px-3 py-2.5 font-medium">Title</th>
                <th className="text-left px-3 py-2.5 font-medium">Status</th>
                <th className="text-left px-3 py-2.5 font-medium w-16">Cov</th>
                <th className="text-left px-3 py-2.5 font-medium">Evidence</th>
              </tr>
            </thead>
            <tbody>
              {(d.control_detail || []).filter(ctrl => !statusFilter || ctrl.status === statusFilter).map((ctrl) => {
                const meta = STATUS_META[ctrl.status] || STATUS_META.not_evaluated;
                const Icon = meta.icon;
                return (
                  <tr key={ctrl.control} className="border-b border-zinc-900/60 hover:bg-zinc-900/30">
                    <td className="px-5 py-2.5 font-mono text-xs text-zinc-300 whitespace-nowrap">{ctrl.control}</td>
                    <td className="px-3 py-2.5 text-zinc-200">{ctrl.title}</td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex items-center gap-1.5 ${meta.cls}`}>
                        <Icon size={14} /> <span className="text-xs">{meta.label}</span>
                      </span>
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs text-zinc-400">{ctrl.coverage_pct}%</td>
                    <td className="px-3 py-2.5 text-xs text-zinc-500">{(ctrl.evidence || [])[0] || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-[11px] text-zinc-600 mt-4 font-mono">
        Coverage computed live from agent telemetry · click a coverage tile to see the devices behind it, or met/partial/gap to filter the table
      </div>

      <DrillPanel metric={drill} onClose={() => setDrill(null)} />
    </div>
  );
}
